import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Description as DocsIcon,
  Extension as ExtensionIcon,
  VpnKey as TokenIcon,
} from '@mui/icons-material';
import { Link } from '@tanstack/react-router';
import { MainLayout } from '../components/MainLayout';
import { getMCPEndpoint } from '../utils/mcp';

function CodeBlock({ value, label, onCopy }: { value: string; label: string; onCopy: () => void }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        {label}
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1, px: 2, py: 1.5,
          bgcolor: 'background.default', overflow: 'hidden',
        }}
      >
        <Typography
          component="pre"
          sx={{ m: 0, flexGrow: 1, overflowX: 'auto', fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre' }}
        >
          {value}
        </Typography>
        <IconButton size="small" onClick={onCopy} aria-label={`Copy ${label}`}>
          <CopyIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Box>
  );
}

export const ExtensionsPage: React.FC = () => {
  const endpoint = useMemo(() => getMCPEndpoint(window.location.origin), []);
  const [message, setMessage] = useState<string | null>(null);
  const config = useMemo(() => JSON.stringify({
    mcpServers: {
      folioworth: {
        url: endpoint,
        headers: { Authorization: 'Bearer fw_your_token_here' },
      },
    },
  }, null, 2), [endpoint]);

  const copy = async (value: string, name: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setMessage(`${name} copied`);
    } catch {
      setMessage(`Could not copy ${name.toLowerCase()}`);
    }
  };

  return (
    <MainLayout>
      <Box sx={{ maxWidth: 900 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <ExtensionIcon color="primary" />
          <Typography variant="h4" fontWeight="bold">Extensions</Typography>
          <Chip label="MCP" size="small" color="primary" variant="outlined" />
        </Stack>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Connect an AI assistant to your FolioWorth household through one lightweight,
          authenticated Model Context Protocol server.
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          Your API token gives an AI the same access you grant here. Start with a read-only
          token. Add write scope only when you want the assistant to create, edit, or delete
          financial records, and keep confirmation prompts enabled in your MCP client.
        </Alert>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Connect in three steps</Typography>
            <Stack spacing={2.5} sx={{ mt: 2 }}>
              <Box>
                <Typography fontWeight={700}>1. Create a dedicated API token</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.25 }}>
                  Name it after the AI client so it is easy to revoke. The plain token is shown
                  once, so copy it before closing the creation notice.
                </Typography>
                <Button component={Link} to="/api-tokens" variant="outlined" size="small" startIcon={<TokenIcon />}>
                  Open API Tokens
                </Button>
              </Box>

              <Box>
                <Typography fontWeight={700}>2. Add a remote MCP server in your AI client</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                  Create a custom or remote MCP connection, paste the server URL, and set the
                  Authorization header to <code>Bearer</code>, one space, then your token. Never
                  put the token in the URL.
                </Typography>
                <Stack spacing={1.5}>
                  <CodeBlock value={endpoint} label="MCP server URL" onCopy={() => copy(endpoint, 'Server URL')} />
                  <CodeBlock
                    value="Authorization: Bearer fw_your_token_here"
                    label="Authentication header"
                    onCopy={() => copy('Authorization: Bearer fw_your_token_here', 'Header')}
                  />
                  <CodeBlock value={config} label="Generic MCP configuration" onCopy={() => copy(config, 'Configuration')} />
                </Stack>
              </Box>

              <Box>
                <Typography fontWeight={700}>3. Enable and test the Extensions tool</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Your client should discover one tool named <code>Extensions</code>. Ask it to
                  “list my FolioWorth accounts.” It will first search the live API catalog, then
                  call the matching route. If your client only supports OAuth and cannot send a
                  custom bearer header, use a client or local MCP bridge that supports headers.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>What the server exposes</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Extensions has a compact catalog action and a request action covering every live
              <code> /api/v1</code> route, including JSON, downloads, and multipart imports. The
              same household isolation, roles, validation, and API-token scopes apply. The MCP
              adapter runs inside the existing Go API with no sidecar service or extra database
              connection.
            </Typography>
            <Button component={Link} to="/api-docs" variant="text" startIcon={<DocsIcon />}>
              Browse REST API documentation
            </Button>
          </CardContent>
        </Card>

        <Snackbar
          open={!!message}
          autoHideDuration={3000}
          onClose={() => setMessage(null)}
          message={message}
        />
      </Box>
    </MainLayout>
  );
};
